import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Card, Container, FormCheck, FormControl, FormSelect } from 'react-bootstrap';
import { LocalizeContext } from 'react-locale-language';
import { toast } from 'react-toastify';
import PercentageInput from '../../form-generator/components/PercentageInput';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { isRtl } from '../../helpers/localization';
import { formatCurrency, formatPercentage } from '../admin/manage-users/customer/strategies/helper';
import { getAllOptionsFields, getAllRSUFields } from './constants';
import { getInitialValues, getRSUOptionsParsedValues } from './helpers';
import { getOptionsCalculations, getRsuCalculations } from './rsuOptionsHelpers';
import FullscreenModal from '../common/FullscreenModal';
import useAppChoices from '../../hooks/useAppChoices';
import useLocalization from '../../hooks/useLocalization';
import {
  Calculator as CalculatorIcon,
  ExpandIcon,
  FoldHorizontalIcon,
  Save,
  ShrinkIcon,
  UnfoldHorizontalIcon
} from 'lucide-react';
import { Button as ButtonTW } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const getRSUCalculatedValues = (allFields, sections, adminCompTable, rsuRawData) => {
  const parsedValues = getRSUOptionsParsedValues(allFields, sections, adminCompTable, rsuRawData);
  return getRsuCalculations(parsedValues);
};

const getOptionsCalculatedValues = (allFields, sections, adminCompTable, rsuRawData) => {
  const parsedValues = getRSUOptionsParsedValues(allFields, sections, adminCompTable, rsuRawData);
  return getOptionsCalculations(parsedValues);
};

const DataRow = ({
  label,
  dataObject,
  objectKey,
  isInput,
  required,
  options,
  sections,
  type,
  onValueChange,
  allFields,
  isRtlLanguage,
  collapsedSections,
  tabIndex,
  totalFieldsCount,
  readOnlyMode
}) => {
  const { translate } = useLocalization();
  return (
    <TableRow className="border-0">
      <TableCell
        className={`py-1 ps-4 text-sm font-medium bg-gray-50 ${isRtlLanguage ? 'text-right' : 'text-left'} calc-column`}
      >
        <b>
          {label}
          {type === 'percentage' && ' (%)'}
        </b>
      </TableCell>
      {sections.map((section, sectionIndex) => (
        <TableCell key={section.id} className={`p-1 text-${isRtlLanguage ? 'end' : 'start'}`}>
          {!collapsedSections.includes(section.id) && (
            <>
              {isInput ? (
                type === 'boolean' ? (
                  <FormCheck
                    tabIndex={sectionIndex * totalFieldsCount + tabIndex}
                    size="sm"
                    disabled={readOnlyMode}
                    checked={dataObject[`${section.id}${objectKey}`]}
                    onChange={e => onValueChange(`${section.id}${objectKey}`, type, e.target.checked)}
                    className="custom-checkbox"
                  />
                ) : type === 'rate' ? (
                  <FormSelect
                    className="rounded-none"
                    tabIndex={sectionIndex * totalFieldsCount + tabIndex}
                    size="sm"
                    disabled={readOnlyMode}
                    value={dataObject[`${section.id}${objectKey}`]}
                    onChange={e => onValueChange(`${section.id}${objectKey}`, type, e.target.value)}
                  >
                    {[
                      'Select...',
                      ...Array(10)
                        .fill(0)
                        .map((a, i) => i + 1)
                    ].map(o => (
                      <option key={o} value={o === 'Select...' ? '' : o}>
                        {o}
                      </option>
                    ))}
                  </FormSelect>
                ) : type === 'percentage' ? (
                  <PercentageInput
                    tabIndex={sectionIndex * totalFieldsCount + tabIndex}
                    className="m-0 rounded-none"
                    size="sm"
                    disabled={readOnlyMode}
                    value={dataObject[`${section.id}${objectKey}`]}
                    onChange={e => onValueChange(`${section.id}${objectKey}`, type, e.target.value)}
                  />
                ) : type === 'dropdown' ? (
                  <FormSelect
                    tabIndex={sectionIndex * totalFieldsCount + tabIndex}
                    size="sm"
                    disabled={readOnlyMode}
                    value={dataObject[`${section.id}${objectKey}`]}
                    className="rounded-none"
                    onChange={e =>
                      onValueChange(
                        `${section.id}${objectKey}`,
                        type,
                        e.target.value === 'select_one' ? null : e.target.value
                      )
                    }
                  >
                    {!required && <option value={'select_one'}>{translate('select_one')}</option>}
                    {options.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </FormSelect>
                ) : (
                  <FormControl
                    autoComplete="off"
                    tabIndex={sectionIndex * totalFieldsCount + tabIndex}
                    className="m-0 rounded-none"
                    as={type === 'text-area' ? 'textarea' : 'input'}
                    type={'text'}
                    disabled={readOnlyMode}
                    size="sm"
                    rows={3}
                    value={dataObject[`${section.id}${objectKey}`]}
                    onChange={e => onValueChange(`${section.id}${objectKey}`, type, e.target.value)}
                  />
                )
              ) : (
                <span className="flex w-full items-center py-1 px-2 bg-gray-100 border border-gray-100 rounded-none">
                  {type === 'percentage'
                    ? formatPercentage(
                        getPercentageValue(
                          allFields,
                          `${section.id}${objectKey}`,
                          dataObject[`${section.id}${objectKey}`]
                        )
                      )
                    : formatCurrency(dataObject[`${section.id}${objectKey}`])}
                </span>
              )}
            </>
          )}
        </TableCell>
      ))}
    </TableRow>
  );
};

const getServerFields = (allFields, sections, values) => {
  return allFields
    .filter(f => f.isInput)
    .flatMap(f => sections.map(s => `${s.id}${f.objectKey}`))
    .reduce((acc, objectKey) => ({ ...acc, [objectKey]: values[objectKey] }), {});
};

const rsuSections = [
  {
    label: 'holding_postponing_tax',
    id: 'postponingTax'
  },
  {
    label: 'paying_tax_investing_somewhere',
    id: 'payingTax'
  }
];

const optionsSections = [
  {
    label: 'options_to_cash_after_two_years',
    id: 'after'
  },
  {
    label: 'options_to_cash_pre_two_years',
    id: 'pre'
  }
];

const Calculator = ({
  title,
  values,
  fullScreenView,
  setFullScreenView,
  onValuesChange,
  showSaveButton,
  updatingCustomer,
  onSaveClick,
  onToggleCollapse,
  allFields,
  sections,
  tabIndexOffset = 0,
  onInputValueChange,
  readOnlyMode
}) => {
  const { translate, langCode } = useContext(LocalizeContext);
  const [collapsedSections, setCollapsedSections] = useState([]);
  const totalFieldsCount = useMemo(() => allFields.length, [allFields]);
  const isRtlLanguage = useMemo(() => isRtl(langCode), [langCode]);

  const onCollapseSectionClick = sectionId => {
    if (collapsedSections.includes(sectionId)) {
      collapsedSections.splice(collapsedSections.indexOf(sectionId), 1);
    } else {
      collapsedSections.push(sectionId);
    }
    onToggleCollapse(collapsedSections);
    setCollapsedSections([...collapsedSections]);
  };

  useEffect(() => {
    if (values?.collapsedSections?.length > 0) {
      setCollapsedSections([...values?.collapsedSections]);
    }
  }, [values]);
  return (
    <Card className={`overflow-hidden rounded-0 border-0 p-2 `}>
      <Card.Header className="bg-white mb-2 border-0 ">
        <div className="d-flex">
          <div
            className={`flex-grow-1 d-flex align-items-center gap-2 font-bold text-${isRtlLanguage ? 'end' : 'start'}`}
          >
            <CalculatorIcon size={16} />
            {translate(title)}
            {fullScreenView ? (
              <ShrinkIcon size={16} onClick={() => setFullScreenView(false)} cursor="pointer" />
            ) : (
              <ExpandIcon size={16} onClick={() => setFullScreenView(true)} cursor="pointer" />
            )}
          </div>
          <div>
            {showSaveButton && !readOnlyMode && (
              <ButtonTW
                disabled={updatingCustomer}
                variant="outline"
                size="sm"
                className="px-2 shadow-none border py-0 border-success text-success rounded-lg hover:bg-[rgb(var(--bs-success-rgb))] hover:text-white"
                onClick={onSaveClick}
              >
                <Save size={10} />
                <span className="align-middle mx-1">{translate('save')}</span>
              </ButtonTW>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className="pt-0 pb-4 scrollbar-visible overflow-auto px-4">
        <Table className="overflow-auto border border-gray-400 px-2">
          <TableHeader className="border-0">
            <TableRow className="border-0 bg-primary">
              <TableHead className="font-bold ps-4 calc-column" />
              {sections.map(section => (
                <TableHead
                  style={!collapsedSections.includes(section.id) ? {} : undefined}
                  key={section.id}
                  className={`p-1 text-end ${!collapsedSections.includes(section.id) ? 'calc-column' : ''}`}
                >
                  <div className="flex px-1 items-center text-white">
                    {!collapsedSections.includes(section.id) && (
                      <div className={`flex-grow-1 mx-1 text-${isRtlLanguage ? 'end' : 'start'}`}>
                        {translate(section.label)}
                      </div>
                    )}
                    <div>
                      <ButtonTW
                        variant="outline"
                        size="sm"
                        className="px-1 m-0 py-0 border-0 shadow-none bg-transparent text-white"
                        onClick={() => onCollapseSectionClick(section.id)}
                      >
                        {collapsedSections.includes(section.id) ? (
                          <UnfoldHorizontalIcon size={12} />
                        ) : (
                          <FoldHorizontalIcon size={12} />
                        )}
                      </ButtonTW>
                    </div>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {allFields.map((rowDetails, index) => (
              <DataRow
                {...rowDetails}
                sections={sections}
                allFields={allFields}
                dataObject={values}
                onValueChange={onInputValueChange}
                isRtlLanguage={isRtlLanguage}
                collapsedSections={collapsedSections}
                tabIndex={tabIndexOffset + index + 1}
                totalFieldsCount={totalFieldsCount}
                readOnlyMode={readOnlyMode}
              />
            ))}
          </TableBody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const RSUOptions = ({ customer, onCustomerUpdate, fromCustomerPortal, adminCompTable, readOnlyMode, assigneUsers }) => {
  const [rsuValues, setRSUValues] = useState({});
  const [optionsValues, setOptionsValues] = useState({});

  const [updatingCustomer, setUpdatingCustomer] = useState(false);
  const { langCode, translate } = useLocalization();
  const [fullScreenRSUView, setFullScreenRSUView] = useState(false);
  const [fullScreenOptionsView, setFullScreenOptionsView] = useState(false);

  const calculatorConfigs = useAppChoices('calculators');
  const rsuCalculatorConfig = useMemo(() => calculatorConfigs?.find(c => c.type === 'rsuCalculator'), [
    calculatorConfigs
  ]);
  const optionsCalculatorConfig = useMemo(() => calculatorConfigs?.find(c => c.type === 'optionsCalculator'), [
    calculatorConfigs
  ]);

  const allRSUFields = useMemo(() => getAllRSUFields(rsuCalculatorConfig?.fields, langCode?.toLowerCase()), [
    rsuCalculatorConfig,
    langCode
  ]);
  const allOptionsFields = useMemo(
    () => getAllOptionsFields(optionsCalculatorConfig?.fields, translate, langCode?.toLowerCase()),
    [optionsCalculatorConfig, langCode, translate]
  );

  useEffect(() => {
    const rsuObject =
      customer?.rsu ||
      rsuSections.reduce(
        (acc, sec) => ({ ...acc, ...getInitialValues(allRSUFields, adminCompTable, false, sec.id) }),
        {}
      );
    setRSUValues({ ...rsuObject, ...getRSUCalculatedValues(allRSUFields, rsuSections, adminCompTable, rsuObject) });
    const optionsObject =
      customer?.options ||
      optionsSections.reduce(
        (acc, sec) => ({ ...acc, ...getInitialValues(allOptionsFields, adminCompTable, false, sec.id) }),
        {}
      );
    setOptionsValues({
      ...optionsObject,
      ...getOptionsCalculatedValues(allOptionsFields, optionsSections, adminCompTable, optionsObject)
    });
  }, [customer]);

  const onSaveClick = async () => {
    toast.info('Saving data...');
    setUpdatingCustomer(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: {
        rsu: {
          ...getServerFields(allRSUFields, rsuSections, rsuValues),
          collapsedSections: rsuValues?.collapsedSections || []
        },
        options: {
          ...getServerFields(allOptionsFields, optionsSections, optionsValues),
          collapsedSections: optionsValues?.collapsedSections || []
        },
        _id: customer['_id']
      }
    });

    setUpdatingCustomer(false);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Updated successfully!');
    onCustomerUpdate(response);
  };

  const onValueChange = (objectKey, currentInputType, newValue, forRSU) => {
    const values = forRSU ? rsuValues : optionsValues;
    const sections = forRSU ? rsuSections : optionsSections;
    const allFields = forRSU ? allRSUFields : allOptionsFields;

    values[objectKey] = !currentInputType && newValue ? formatCurrency(newValue, true, false) : newValue;

    if (objectKey.startsWith(sections[0].id)) {
      const keyWithoutSection = objectKey.replace(sections[0].id, '');
      const field = allFields.find(f => f.objectKey === keyWithoutSection && f.linkedWithFirstSection);
      if (field) {
        values[`${sections[1].id}${keyWithoutSection}`] =
          !currentInputType && newValue ? formatCurrency(newValue, true, false) : newValue;
      }
    }

    if (forRSU) {
      setRSUValues({ ...values, ...getRSUCalculatedValues(allFields, sections, adminCompTable, values) });
    } else {
      setOptionsValues({ ...values, ...getOptionsCalculatedValues(allFields, sections, adminCompTable, values) });
    }
  };

  return (
    <>
      <Container fluid className="h-100 p-0 text-start">
        <CalculatorWithFullscreen
          title="rsu_calculator"
          values={rsuValues}
          fullScreenView={fullScreenRSUView}
          setFullScreenView={setFullScreenRSUView}
          showSaveButton={customer}
          updatingCustomer={updatingCustomer}
          onSaveClick={onSaveClick}
          onValuesChange={setRSUValues}
          adminCompTable={adminCompTable}
          allFields={allRSUFields}
          sections={rsuSections}
          onInputValueChange={(objectKey, currentInputType, newValue) =>
            onValueChange(objectKey, currentInputType, newValue, true)
          }
          onToggleCollapse={collapsedSections => setRSUValues({ ...rsuValues, collapsedSections })}
          readOnlyMode={readOnlyMode}
        />
        <CalculatorWithFullscreen
          title="options_calculator"
          values={optionsValues}
          fullScreenView={fullScreenOptionsView}
          setFullScreenView={setFullScreenOptionsView}
          showSaveButton={customer}
          updatingCustomer={updatingCustomer}
          onSaveClick={onSaveClick}
          onValuesChange={setOptionsValues}
          adminCompTable={adminCompTable}
          allFields={allOptionsFields}
          sections={optionsSections}
          tabIndexOffset={allRSUFields.length * 2}
          onInputValueChange={(objectKey, currentInputType, newValue) =>
            onValueChange(objectKey, currentInputType, newValue, false)
          }
          onToggleCollapse={collapsedSections => setOptionsValues({ ...optionsValues, collapsedSections })}
          readOnlyMode={readOnlyMode}
        />
      </Container>
    </>
  );
};

export default RSUOptions;

const CalculatorWithFullscreen = ({
  title,
  values,
  fullScreenView,
  setFullScreenView,
  onValuesChange,
  showSaveButton,
  updatingCustomer,
  onSaveClick,
  onToggleCollapse,
  allFields,
  sections,
  tabIndexOffset = 0,
  onInputValueChange,
  readOnlyMode
}) => {
  if (fullScreenView) {
    return (
      <FullscreenModal show={fullScreenView} onHide={() => setFullScreenView(false)}>
        <Calculator
          title={title}
          values={values}
          fullScreenView={fullScreenView}
          setFullScreenView={setFullScreenView}
          onValuesChange={onValuesChange}
          showSaveButton={showSaveButton}
          updatingCustomer={updatingCustomer}
          onSaveClick={onSaveClick}
          onToggleCollapse={onToggleCollapse}
          allFields={allFields}
          sections={sections}
          tabIndexOffset={tabIndexOffset}
          onInputValueChange={onInputValueChange}
          readOnlyMode={readOnlyMode}
        />
      </FullscreenModal>
    );
  }

  return (
    <Calculator
      title={title}
      values={values}
      fullScreenView={fullScreenView}
      setFullScreenView={setFullScreenView}
      onValuesChange={onValuesChange}
      showSaveButton={showSaveButton}
      updatingCustomer={updatingCustomer}
      onSaveClick={onSaveClick}
      onToggleCollapse={onToggleCollapse}
      allFields={allFields}
      sections={sections}
      tabIndexOffset={tabIndexOffset}
      onInputValueChange={onInputValueChange}
      readOnlyMode={readOnlyMode}
    />
  );
};
